import React, {FormEvent, useEffect, useMemo, useState} from "react";
import {getPrimitiveArray, loadPdf} from "../../../../helpers/utils/Utils";
import Form from "../../../../components/common/form/Form";
import Modal from "../../../../components/common/form/Modal";
import InputField from "../../../../components/common/form/InputField";
import ButtonTray from "../../../../components/common/form/ButtonTray";
import {useGenerateSOAPdfServiceMutation, useSendSOAPdfByEmailServiceMutation} from "../../ordersApi";
import CommonChosenInput from "../Sections/CommonChosenInput";
import {useNavigate} from "react-router-dom";

interface AccountStatementModalProps {
    show: boolean;
    setShow: (show: boolean) => void;
    certificate: any; // Replace 'any' with a proper type for certificate
    showAccount: number;
    storageData: any; // Replace 'any' with a proper type for storageData
    orderData: any; // Replace 'any' with a proper type for orderData
    productId: string;
}

interface EmailRecipient {
    label: string;
    value: string;
}

export default function AccountStatementModal({
                                                  show,
                                                  setShow,
                                                  certificate,
                                                  showAccount,
                                                  storageData,
                                                  orderData,
                                                  productId
                                              }: AccountStatementModalProps) {

    const navigate = useNavigate();
    const [disable, setDisable] = useState(false);
    const [subject, setSubject] = useState("");
    const [to, setTo] = useState<EmailRecipient[]>([]);
    const [cc, setCC] = useState<EmailRecipient[]>([]);
    const [customerOrderNumber, setCustomerOrderNumber] = useState("");
    const [cadNumber, setCadNumber] = useState("");
    const [subdivisionName, setSubdivisionName] = useState("");
    const [address, setAddress] = useState("");
    const [legalDescription, setLegalDescription] = useState("");
    const [ownerName, setOwnerName] = useState("");
    const [buyerName, setBuyerName] = useState("");
    const [showIframe, setShowIframe] = useState(false);
    const [fileName, setFileName] = useState<string | null>(null);
    const [generatePDf, generatePdfApi] = useGenerateSOAPdfServiceMutation();
    const [sendPdf, sendPdfApi] = useSendSOAPdfByEmailServiceMutation();

    const product = useMemo(() => orderData?.data?.products?.find(
        (product: any) => product.id === productId
    ), [orderData?.data?.products]);
    
    useEffect(() => {
        setCadNumber(certificate?.accounts[showAccount].accountNumber);
        setSubdivisionName(certificate?.accounts[showAccount].propertySubdivision);
        setAddress(certificate?.accounts[showAccount].situsAddress);
        setLegalDescription(certificate?.accounts[showAccount].legalDescription);
        setOwnerName(certificate?.accounts[showAccount].sellers);
        setBuyerName(certificate?.accounts[showAccount].buyers);
        setCustomerOrderNumber(certificate?.accounts[showAccount].customerOrderNumber);

        const defaultToEmail = certificate?.accounts[showAccount]?.managementCompany?.email;
        const newTo = [...to];
        if (defaultToEmail) {
            newTo.push({
                label: defaultToEmail,
                value: defaultToEmail
            });
            setTo(newTo);
        }

        const newCC = [...cc];
        newCC.push({label: storageData?.email, value: storageData?.email});

        if (product?.soaRequested) {
            const soaNotificationEmail = orderData?.data?.branchDTO?.soaNotificationEmail;
            if (soaNotificationEmail) {
                newCC.push({label: soaNotificationEmail, value: soaNotificationEmail});
            }
        }
        setCC(newCC);
        setSubject("Association Statement of Account Request - " + getValidSubject(
            certificate?.accounts[showAccount]?.customerOrderNumber,
            certificate?.accounts[showAccount]?.associationName,
            certificate?.accounts[showAccount]?.situsAddress
        ));
    }, [showAccount]);


    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setDisable(true);

        try {
            if (!showIframe) {
                const obj = {
                    orderNumber: customerOrderNumber,
                    cadNumber: cadNumber,
                    subdivisionName: subdivisionName,
                    address: address,
                    legalDescription: legalDescription,
                    ownerName: ownerName,
                    buyerName: buyerName,
                };

                const response = await generatePDf({
                    orderId: orderData?.data?.id,
                    productId: product?.id,
                    customerId: orderData?.data?.customerId,
                    tenant: orderData?.data?.tenant,
                    tgxId: orderData?.data?.tgxId,
                    obj: obj,
                });

                if (response?.data?.success) {
                    setFileName(response.data.fileName);
                    loadPdf(response.data.id);
                    setShowIframe(true);
                } else {

                    console.error("PDF generation failed", response);
                }

            } else {
                await sendPdf({
                    orderId: orderData?.data?.id,
                    toAddress: getPrimitiveArray(to, "value"),
                    ccAddress: getPrimitiveArray(cc, "value"),
                    mailSubject: subject,
                    filename: fileName,
                });
                setShow(false);
                navigate("/orders/" + orderData?.data?.id)
            }
        } catch (error) {
            console.error("Error in handleSubmit:", error);
            // Optionally show a user-friendly error message here
        } finally {
            setDisable(false); // Re-enable button after process completes
        }
    };


    function getValidSubject(customerOrderNumber: string, associationName: string, address: string): string {
        if (customerOrderNumber && associationName && address) {
            return `${certificate?.accounts[showAccount]?.associationName} - ${certificate?.accounts[showAccount]?.situsAddress} - ${certificate?.accounts[showAccount]?.customerOrderNumber}`;
        } else if (customerOrderNumber && associationName) {
            return `${certificate?.accounts[showAccount]?.associationName} - ${certificate?.accounts[showAccount]?.customerOrderNumber}`;
        } else if (associationName && address) {
            return `${certificate?.accounts[showAccount]?.associationName} - ${certificate?.accounts[showAccount]?.situsAddress}`;
        } else if (customerOrderNumber && address) {
            return `${certificate?.accounts[showAccount]?.situsAddress} - ${certificate?.accounts[showAccount]?.customerOrderNumber}`;
        } else {
            return certificate?.accounts[showAccount]?.customerOrderNumber || '';
        }
    }

    return (
        <Modal
            title={"Statement of Account"}
            show={show}
            setShow={setShow}
            hoa={true}
            className="w-2/4 bg-text-0 rounded-lg shadow-lg "
            fields={
                <>
                    <Form onSubmit={handleSubmit} fields={
                        <>
                            <div
                                className="modal-body max-h-[calc(100vh-100px)] overflow-auto [-ms-overflow-style:none] [scrollbar-width:none]">
                                {!showIframe && <>
                                    <div className={"border border-[#b0aaaa] max-w-full pt-4 relative w-full mt-4"}>
                                        <label
                                            className={"bg-[white] text-[15px] font-bold absolute left-[15px] top-[-12px]"}>Email
                                            Settings</label>


                                        <div className="p-3">
                                            <InputField placeholder="Enter Subject..."
                                                        label="Subject"
                                                        required
                                                        value={subject} onChange={(e) => setSubject(e.target.value)}
                                                        name="subject"
                                                        type="text"
                                            />

                                            <div className="grid grid-cols-12 gap-3 py-2">
                                                <div className="col-span-3 font-medium text-sm pt-2"><label>*To</label>
                                                </div>
                                                <div className="col-span-9">
                                                    <CommonChosenInput
                                                        placeholder={"abc@xyz.com..."}
                                                        state={to}
                                                        setState={setTo}
                                                        isRequired={true}
                                                    />
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-12 gap-3 py-2">
                                                <div className="col-span-3 font-medium text-sm pt-2"><label>*CC</label>
                                                </div>
                                                <div className="col-span-9">
                                                    <CommonChosenInput
                                                        placeholder={"abc@xyz.com..."}
                                                        state={cc}
                                                        setState={setCC}
                                                        isRequired={true}
                                                    />
                                                </div>
                                            </div>

                                        </div>

                                    </div>
                                    <div className={"border border-[#b0aaaa] max-w-full pt-4 relative w-full mt-5"}>
                                        <label
                                            className={"bg-[white] text-[15px] font-bold absolute left-[15px] top-[-12px]"}>Form
                                            Settings</label>


                                        <div className="p-3">
                                            <InputField placeholder="Enter Order Number.."
                                                        label="Order Number"
                                                        required
                                                        value={customerOrderNumber}
                                                        onChange={(e) => setCustomerOrderNumber(e.target.value)}
                                                        name="customerOrderNumber"
                                                        type="text"
                                            />

                                            <InputField type="text"
                                                        placeholder="Enter CAD Number..."
                                                        name="cadNumber"
                                                        value={cadNumber}
                                                        label="CAD Number"
                                                        onChange={(e) => setCadNumber(e.target.value)}
                                                        required={true}/>

                                            <InputField label="Subdivision Name" type="text"
                                                        placeholder="Enter Subdivision Name..."
                                                        name="subdivisionName"
                                                        value={subdivisionName}
                                                        onChange={(e) => setSubdivisionName(e.target.value)}
                                                        required={true}/>

                                            <InputField label="Address" type="text"
                                                        placeholder="Enter Address..."
                                                        name="pdfHeaderTitle"
                                                        value={address} onChange={(e) => setAddress(e.target.value)}
                                                        required={true}/>

                                            <InputField label="Legal Description" type="text"
                                                        placeholder="Enter Legal Description..."
                                                        name="legalDescription"
                                                        value={legalDescription}
                                                        onChange={(e) => setLegalDescription(e.target.value)}
                                                        required={true}/>

                                            <InputField label="Owner Name" type="text"
                                                        placeholder="Enter Owner Name..."
                                                        name="ownerName"
                                                        value={ownerName}
                                                        onChange={(e) => setOwnerName(e.target.value)}
                                                        required={true}/>
                                            <InputField type="text"
                                                        placeholder="Enter Buyer Name..."
                                                        name="buyerName"
                                                        value={buyerName}
                                                        label="Buyer Name"
                                                        onChange={(e) => setBuyerName(e.target.value)}
                                                        required={true}/>
                                        </div>

                                    </div>
                                </>
                                }
                                {showIframe && <div className={"mt-3"}>
                                    <iframe src=""
                                            style={{
                                                width: '100%', minHeight: '900px', overflow: 'visible'
                                            }}
                                            width="100%"
                                            height="100%"
                                    />
                                </div>}

                            </div>
                            <div className={"modal-footer border-t border-text-1 mt-3"}>
                                {!showIframe ? (
                                    <ButtonTray
                                        buttons={[
                                            {
                                                buttonProps: {
                                                    btnText: "Cancel",
                                                    onClick: () => setShow(false),
                                                    type: "cancel",
                                                },
                                                buttonType: "button",
                                            },
                                            {
                                                buttonProps: {
                                                    btnText: "Generate",
                                                    type: "submit",
                                                    isDisabled: disable,
                                                    isHoa: true,
                                                },
                                                buttonType: "button",
                                            },
                                        ]}
                                        align="end"
                                        gap={2}
                                    />
                                ) : (
                                    <ButtonTray
                                        buttons={[
                                            {
                                                buttonProps: {
                                                    btnText: "Back",
                                                    onClick: () => setShowIframe(false),
                                                    type: "cancel",
                                                },
                                                buttonType: "button",
                                            },
                                            {
                                                buttonProps: {
                                                    btnText: "Cancel",
                                                    onClick: () => setShow(false),
                                                    type: "cancel",
                                                },
                                                buttonType: "button",
                                            },
                                            {
                                                buttonProps: {
                                                    btnText: "Submit",
                                                    type: "submit",
                                                    isDisabled: disable,
                                                    isHoa: true,
                                                },
                                                buttonType: "button",
                                            },
                                        ]}
                                        align="end"
                                        gap={2}
                                    />
                                )}

                            </div>


                        </>
                    }
                    />

                </>
            }/>

    );
}